import { EnterOutlined } from '@ant-design/icons';
import { type HTMLAttributes, useContext, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import { Flex } from '@/components/base';
import { radius } from '@/styles/sizes';

import { KeyboardNavigatorContext } from './KeyboardNavigator';

const KeyboardNavigatorItem = (
  props: HTMLAttributes<HTMLDivElement> & {
    selected?: boolean;
  },
) => {
  const ref = useRef<HTMLDivElement>(null);
  const { listRef } = useContext(KeyboardNavigatorContext);

  useEffect(() => {
    if (!props.selected || !ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // 요소가 보이지 않을 때만 scrollIntoView 실행
        if (!entry.isIntersecting) {
          const parent = listRef?.current;
          const parentRect = parent?.getBoundingClientRect();
          const elementRect = entry.boundingClientRect;
          const isAboveViewport =
            elementRect.bottom < (parentRect?.bottom || 0);

          // 요소가 viewport 위로 벗어났는지 아래로 벗어났는지 확인
          ref.current?.scrollIntoView({
            block: isAboveViewport ? 'start' : 'end',
          });
        }
      },
      {
        root: null, // viewport 기준
        threshold: 0.9, // 요소가 100% 보일 때만 intersecting으로 간주
      },
    );

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [listRef, props.selected]);

  return (
    <SelectableItemStyle {...props} selected={props.selected} ref={ref}>
      {props.children}
      <EnterOutlined className="enter-icon" />
    </SelectableItemStyle>
  );
};

const SelectableItemStyle = styled(Flex)<{ selected?: boolean }>`
  justify-content: space-between;
  padding: 8px 12px;
  background-color: ${(props) => props.theme.colors.gray100};
  border-radius: ${radius.token.sm}px;
  cursor: pointer;
  .enter-icon {
    opacity: 0;
    font-size: 16px;
    color: ${(props) => props.theme.colors.gray700};
  }
  ${(props) =>
    props.selected &&
    css`
      background-color: ${(props) => props.theme.colors.gray200};
      .enter-icon {
        opacity: 1;
      }
    `}
`;

export default KeyboardNavigatorItem;
